/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./OrgSetup.css";
import PropTypes from "prop-types";
import OrgSetupLogo from "./OrgSetupLogo/OrgSetupLogo";
import OrgSetupDoc from "./OrgSetupDoc/OrgSetupDoc";
import OrgSetupKyc from "./OrgSetupKyc/OrgSetupKyc";
import OrgSetupBank from "./OrgSetupBank/OrgSetupBank";
import OrgSetupConfirm from "./OrgSetupConfirm/OrgSetupConfirm";
import { simpleFetch } from "../../restWrapper";
import { PLATFORM } from "../../config";

const maxPage = 5;
const minPage = 0;

class OrgSetup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 0,
      logo: "",
      files: "",
      bankAc: "",
      loading: true,
      completedSetup: false,
      pageTextObj: [
        "",
        this.props.languageObj.OrgSetupLogo,
        this.props.languageObj.OrgSetupDoc,
        this.props.languageObj.OrgSetupBank,
        this.props.languageObj.OrgSetupKyc,
        this.props.languageObj.OrgSetupConfirm,
      ],
    };
  }
  componentDidMount() {
    const path = this.props.location.pathname;
    const token = path.split("/OrgSetup/")[1];

    this.getOrgData().then((res) => {
      if (res.status !== 200) {
        this.props.history.push("/ErrorPrompt");
        this.props.orgLogout();
      }
      try {
        switch (res.body.organizationObj.organizationPermitted) {
          case "approved":
            this.props.history.push("/AccountOverview");
            break;
          case "declined":
            this.props.history.push("/OrgDecline");
            break;
          case "pending":
            try {
              const completedSetup = res.body.organizationObj.completedSetup;
              this.setState({
                completedSetup: completedSetup,
              });
              if (!isNaN(token) && completedSetup) {
                if (token >= minPage && token <= maxPage) {
                  this.setState({
                    page: Number(token),
                  });
                }
              } else {
                this.setState({
                  page: 0,
                });
              }
            } catch (e) {
              this.setState({
                page: 0,
              });
            }
            this.setState({
              loading: false,
            });
            if (
              res.body.organizationObj.completedSetup &&
              this.state.page === 0
            ) {
              this.props.history.push({
                pathname: "/OrgSetup/" + maxPage,
              });
              this.setState({
                page: maxPage,
              });
            }
            break;
          default:
            this.props.history.push("/ErrorPrompt");
            this.props.orgLogout();
        }
      } catch (e) {
        this.props.orgLogout();
      }
    });
  }

  getOrgData() {
    return simpleFetch("/organizations", "GET", {}).then((res) => {
      let logo = "";
      try {
        logo = res.body.organizationObj.logo;
      } catch (e) {
        logo = "";
      } finally {
        this.setState({
          logo: logo,
        });
      }
      return res;
    });
  }

  render() {
    const languageObj = this.props.languageObj;
    const page = this.state.page;
    const lngObj = this.state.pageTextObj[page];
    if (this.state.loading || !languageObj) {
      return <this.props.RenderFullPageLoading />;
    }
    const nextBtn = () => {
      const completedSetup = this.state.completedSetup;
      const bool = completedSetup
        ? maxPage - 1 >= this.state.page + 1
        : maxPage >= this.state.page + 1;
      const page = bool ? this.state.page + 1 : maxPage;
      this.props.history.push({
        pathname: "/OrgSetup/" + page,
      });
      this.setState({
        page: page,
      });
    };
    // CP-304: bank and KYC can be left for later — an organization can edit
    // its profile and publish impact without them, and resume payment setup
    // from here whenever it is ready. Skipping jumps to confirm rather than
    // stepping forward, so deferring at the bank step does not land on KYC.
    //
    // completedSetup is what lets an organization out of this flow, so the
    // navigation waits on the write landing. Moving on regardless would put
    // it back here at its next sign-in with nothing explaining why.
    const skipPaymentsBtn = () => {
      simpleFetch("/organizations", "PUT", {
        completedSetup: true,
      })
        .then((res) => {
          if (!res || (res.status !== 200 && res.status !== 204)) {
            this.props.history.push("/ErrorPrompt");
            return false;
          }
          this.props.history.push({
            pathname: "/OrgSetup/" + maxPage,
          });
          this.setState({
            page: maxPage,
            completedSetup: true,
          });
          return true;
        })
        .catch(() => {
          this.props.history.push("/ErrorPrompt");
          return false;
        });
    };
    const editBtn = () => {
      const page = 1;
      this.props.history.push({
        pathname: "/OrgSetup/" + page,
      });
      this.setState({
        page: page,
      });
    };
    const backBtn = () => {
      const completedSetup = this.state.completedSetup;
      const backPage = this.state.page - 1;
      const page =
        minPage < backPage ? backPage : completedSetup ? maxPage : minPage;

      this.setState({
        page: page,
      });

      if (page === 0) {
        this.props.history.push({
          pathname: "/OrgSetup",
        });
      } else {
        this.props.history.push({
          pathname: "/OrgSetup/" + page,
        });
      }
      this.setState({
        page: page,
      });
    };

    const RenderOrgSetup = () => (
      <div className="OrgSetup-view">
        <div className="OrgSetup-center">
          <div className="OrgSetup-header">
            <div className={PLATFORM === "plug" ? "OrgSetup-PlugLogo" : "OrgSetup-CheckPlusLogo"} />

            <div className="OrgSetup-logoutBtn" onClick={this.props.orgLogout}>
              <div className="OrgSetup-logoutIcn"></div>
            </div>
          </div>

          <div className="OrgSetup-centerContainer">
            <div className="OrgSetup-prompt">
              <div className="OrgSetup-documentContainer">
                <div className="OrgSetup-documentIcn"></div>
                <div className="OrgSetup-heading">{languageObj.text1}</div>
                <div className="OrgSetup-text">{languageObj.text2}</div>
              </div>
              <div className="OrgSetup-completedIcn" />
            </div>

            <div className="OrgSetup-spaceInBetween"></div>

            <div className="OrgSetup-prompt">
              <div className="OrgSetup-loginContainer">
                <div className="OrgSetup-mailVerifyIcn"></div>
                <div className="OrgSetup-heading">{languageObj.text3}</div>
                <div className="OrgSetup-completedIcn" />
              </div>
            </div>
            <div className="OrgSetup-spaceInBetween"></div>

            <div className="OrgSetup-prompt">
              <div className="OrgSetup-loginContainer">
                <div className="OrgSetup-documentIcn"></div>
                <div className="OrgSetup-heading">{languageObj.text4}</div>
                <div className="OrgSetup-text">{languageObj.text5}</div>

                <div className="OrgSetup-nextBtn" onClick={nextBtn}>
                  <div className="OrgSetup-nextIcnContainer">
                    <div className="OrgSetup-nextIcn" />
                  </div>

                  <div className="OrgSetup-nextText">
                    {languageObj.submitBtn}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
    switch (page) {
      case 1:
        return (
          <OrgSetupLogo
            languageObj={lngObj}
            backBtn={backBtn}
            nextBtn={nextBtn}
            getOrgLogo={this.state.logo}
            RenderFullPageLoading={this.props.RenderFullPageLoading}
            orgLogout={this.props.orgLogout}
          />
        );

      case 2:
        return (
          <OrgSetupDoc
            languageObj={lngObj}
            backBtn={backBtn}
            nextBtn={nextBtn}
            RenderFullPageLoading={this.props.RenderFullPageLoading}
            orgLogout={this.props.orgLogout}
          />
        );
      case 3:
        return (
          <OrgSetupBank
            languageObj={lngObj}
            backBtn={backBtn}
            nextBtn={nextBtn}
            skipPaymentsBtn={skipPaymentsBtn}
            RenderFullPageLoading={this.props.RenderFullPageLoading}
            orgLogout={this.props.orgLogout}
          />
        );
      case 4:
        return (
          <OrgSetupKyc
            languageObj={lngObj}
            backBtn={backBtn}
            nextBtn={nextBtn}
            skipPaymentsBtn={skipPaymentsBtn}
            RenderFullPageLoading={this.props.RenderFullPageLoading}
            orgLogout={this.props.orgLogout}
          />
        );
      case 5:
        return (
          <OrgSetupConfirm
            languageObj={lngObj}
            editBtn={editBtn}
            orgLogout={this.props.orgLogout}
          />
        );
      default:
        return <RenderOrgSetup />;
    }
  }
}

OrgSetup.propTypes = {
  orgLogout: PropTypes.func,
  languageObj: PropTypes.object,
  RenderFullPageLoading: PropTypes.func,
};

export default OrgSetup;
